import { useEffect, useRef, useState } from 'react'
import { DedicatedServerPrice } from '../../../api/bareMetalTypes'
import dedicatedPreorderService from '../../../api/dedicatedPreorderService'
import { PreorderPriceParams } from '../../../api/dedicatedPreorderTypes'

export default function usePreorderPrice(params?: PreorderPriceParams) {
  const query = params ? JSON.stringify(params) : ''
  const [attempt, setAttempt] = useState(0)
  const prices = useRef(new Map<string, DedicatedServerPrice>())
  const [request, setRequest] = useState<{ key: string; signal: AbortSignal; status: 'loading' | 'done' | 'failed' }>()
  const key = `${attempt}:${query}`

  useEffect(() => {
    if (!query || prices.current.has(query)) return
    let active = true
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      setRequest({ key, signal: controller.signal, status: 'loading' })
      try {
        const response = await dedicatedPreorderService.getPrice(JSON.parse(query), controller.signal)
        if (active) {
          prices.current.set(query, response.data)
          setRequest({ key, signal: controller.signal, status: 'done' })
        }
      } catch {
        if (active) setRequest({ key, signal: controller.signal, status: 'failed' })
      }
    }, 300)
    return () => {
      active = false
      clearTimeout(timer)
      controller.abort()
    }
  }, [query, key])

  const current = request?.key === key && !request.signal.aborted ? request : undefined
  const price = prices.current.get(query)
  const pending = Boolean(query && !price && current?.status !== 'done' && current?.status !== 'failed')
  const loading = pending && current?.status === 'loading'
  return {
    price,
    waiting: pending && !loading,
    loading,
    failed: current?.status === 'failed',
    retry: () => setAttempt(value => value + 1),
  }
}
